import React, { useState } from "react";
import { useBaixaReceivable, useDeleteReceivable, useGenerateReceivables, useReceivables } from "../../api/receivables";
import { StatusTag } from "../../components/StatusTag";
import { PaginationBar } from "../../components/PaginationBar";
import { BaixaModal } from "../../components/BaixaModal";
import { DeleteIconButton, EditIconButton } from "../../components/RowIconButton";
import { formatBRL, formatDate } from "../../utils/money";
import { pillBtnStyle } from "../../utils/pillButton";
import { NewReceivableModal } from "./NewReceivableModal";
import { EditReceivableModal } from "./EditReceivableModal";
import { useToast } from "../../components/ToastProvider";
import { Receivable } from "../../types/dtos";

const FILTERS: Array<{ label: string; status?: string }> = [
  { label: "Todos" },
  { label: "Em aberto", status: "EM_ABERTO" },
  { label: "Vencido", status: "VENCIDO" },
  { label: "Pago", status: "PAGO" },
];

export default function ReceivablesPage() {
  const [filter, setFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [baixaTarget, setBaixaTarget] = useState<{ id: number; code: string } | null>(null);
  const [editing, setEditing] = useState<Receivable | null>(null);
  const { data, isLoading, isError } = useReceivables({ page, pageSize: 7, status: filter });
  const baixa = useBaixaReceivable();
  const deleteReceivable = useDeleteReceivable();
  const generate = useGenerateReceivables();
  const toast = useToast();

  const totalFiltrado = (data?.data ?? []).reduce((acc, r) => acc + Number(r.value), 0);

  async function handleGenerate() {
    try {
      const result = await generate.mutateAsync();
      toast(
        result.created > 0
          ? `${result.created} título(s) gerado(s) a partir dos contratos vigentes.`
          : "Nenhum contrato com cobrança pendente no momento."
      );
    } catch {
      toast("Não foi possível gerar os títulos.");
    }
  }

  async function handleBaixa(input: { settlementTypeId: number; paidAt: string }) {
    if (!baixaTarget) return;
    try {
      await baixa.mutateAsync({ id: baixaTarget.id, ...input });
      toast(`Baixa de ${baixaTarget.code} registrada — caixa atualizado.`);
      setBaixaTarget(null);
    } catch {
      toast("Não foi possível registrar a baixa.");
    }
  }

  async function handleDelete(id: number, code: string) {
    if (!window.confirm(`Excluir o título ${code}? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteReceivable.mutateAsync(id);
      toast(`${code} · título excluído.`);
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast(message ?? "Não foi possível excluir — cancele em vez disso.");
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="row g-2">
        <div className="col-md-3">
          <label className="nt-field-label">Situação</label>
          <select
            className="form-select"
            value={filter ?? ""}
            onChange={(e) => {
              setFilter(e.target.value || undefined);
              setPage(1);
            }}
          >
            {FILTERS.map((f) => (
              <option key={f.label} value={f.status ?? ""}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 d-flex flex-column">
          <label className="nt-field-label">Total filtrado</label>
          <div className="form-control d-flex align-items-center nt-tabular" style={{ background: "var(--sp-grey-050)" }}>
            {formatBRL(totalFiltrado)}
          </div>
        </div>
        <div className="col-md-3">
          <label className="nt-field-label d-none d-md-block">&nbsp;</label>
          <button type="button" className="btn btn-outline-secondary w-100" disabled={generate.isPending} onClick={handleGenerate}>
            Gerar títulos do mês
          </button>
        </div>
        <div className="col-md-3">
          <label className="nt-field-label d-none d-md-block">&nbsp;</label>
          <button type="button" className="btn btn-primary w-100" onClick={() => setShowNew(true)}>
            Novo título
          </button>
        </div>
      </div>

      {isLoading && <div className="text-secondary">Carregando…</div>}
      {isError && <div className="text-danger">Não foi possível carregar os títulos.</div>}

      {data && (
        <div className="nt-table-wrap" style={{ overflowX: "auto" }}>
          <table className="table nt-table-fade table-striped align-middle">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Título</th>
                <th>Cliente</th>
                <th style={{ width: 150 }}>Referência</th>
                <th style={{ width: 120 }}>Vencimento</th>
                <th className="text-end" style={{ width: 120 }}>Valor</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 200 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((r) => (
                <tr key={r.id} className={r.status === "VENCIDO" ? "nt-row-late" : undefined}>
                  <td className="nt-tabular">{r.code}</td>
                  <td style={{ fontFamily: "var(--font-heading)" }}>{r.customer.razaoSocial}</td>
                  <td>{r.reference}</td>
                  <td className="nt-tabular">{formatDate(r.dueDate)}</td>
                  <td className="text-end nt-tabular">{formatBRL(r.value)}</td>
                  <td>
                    <StatusTag status={r.status} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      {r.status !== "PAGO" && r.status !== "CANCELADO" && <EditIconButton onClick={() => setEditing(r)} />}
                      {r.status !== "PAGO" && (
                        <button
                          type="button"
                          style={pillBtnStyle("#000000")}
                          onClick={() => setBaixaTarget({ id: r.id, code: r.code })}
                        >
                          Registrar baixa
                        </button>
                      )}
                      <DeleteIconButton onClick={() => handleDelete(r.id, r.code)} disabled={deleteReceivable.isPending} />
                    </div>
                  </td>
                </tr>
              ))}
              {data.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-secondary text-center py-4">
                    Nenhum título encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && <PaginationBar page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />}

      <NewReceivableModal show={showNew} onClose={() => setShowNew(false)} />
      <EditReceivableModal receivable={editing} onClose={() => setEditing(null)} />
      <BaixaModal
        show={!!baixaTarget}
        title={`Registrar baixa · ${baixaTarget?.code ?? ""}`}
        confirmLabel="Registrar baixa"
        onClose={() => setBaixaTarget(null)}
        onConfirm={handleBaixa}
        loading={baixa.isPending}
      />
    </div>
  );
}
